import React from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Chip,
  OutlinedInput,
} from '@mui/material';
import { AudienceFeedback } from '../../types/PostProduction';

interface AudienceFeedbackSectionProps {
  audienceFeedback: AudienceFeedback;
  editMode: boolean;
  onChange: (field: string, value: any) => void;
}

const AudienceFeedbackSection: React.FC<AudienceFeedbackSectionProps> = ({
  audienceFeedback,
  editMode,
  onChange,
}) => {
  // Age distribution options from proposal
  const ageDistributionOptions = [
    'Under 25',
    '25-35',
    '36-45',
    '46-55',
    '56-65',
    '66+',
    'Mixed (all ages)'
  ];

  // Audience familiarity options from proposal
  const audienceFamiliarityOptions = [
    'Mostly regulars',
    'Mostly first-timers',
    'Mix of both',
    'Unknown'
  ];

  // Audience origin options from proposal
  const audienceOriginOptions = [
    'Local (Dublin area)',
    'National (Ireland)',
    'International tourists',
    'Mix',
    'Unknown'
  ];

  // Noteworthy quick capture options from proposal
  const noteworthyCategories = [
    'Merch sales',
    'New audience members mentioned returning',
    'Accessibility accommodations used',
    'Special guest appearance',
    'Other notable event'
  ];

  // Complaint categories from proposal
  const complaintCategories = [
    'Bar service',
    'Sound quality',
    'Temperature/comfort',
    'Seating',
    'Accessibility',
    'None',
    'Other'
  ];

  // Suggestion categories from proposal
  const suggestionCategories = [
    'Earlier start time',
    'Different day of week',
    'Pricing adjustment',
    'Longer/shorter show',
    'Improved facilities',
    'None',
    'Other'
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Audience Feedback
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <Typography component="legend">Overall Rating</Typography>
            <Rating
              name="audience-rating"
              value={audienceFeedback.overallRating}
              precision={0.5}
              onChange={(e, value) => onChange('overallRating', value || 0)}
              disabled={!editMode}
            />
          </Box>

          {/* NEW: Age Distribution (Multiple Select) */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Age Distribution</InputLabel>
            <Select
              multiple
              value={audienceFeedback.ageDistribution || []}
              onChange={(e) => onChange('ageDistribution', e.target.value)}
              input={<OutlinedInput label="Age Distribution" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              disabled={!editMode}
            >
              {ageDistributionOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* NEW: Audience Familiarity (Single Select) */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Audience Familiarity</InputLabel>
            <Select
              value={audienceFeedback.audienceFamiliarity || ''}
              onChange={(e) => onChange('audienceFamiliarity', e.target.value)}
              label="Audience Familiarity"
              disabled={!editMode}
            >
              {audienceFamiliarityOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* NEW: Audience Origin (Single Select) */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Audience Origin</InputLabel>
            <Select
              value={audienceFeedback.audienceOrigin || ''}
              onChange={(e) => onChange('audienceOrigin', e.target.value)}
              label="Audience Origin"
              disabled={!editMode}
            >
              {audienceOriginOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* EXISTING: Audience Demographics Free Text (Backwards Compatible) */}
          <TextField
            label="Additional Demographics Notes"
            multiline
            rows={3}
            fullWidth
            value={audienceFeedback.audienceDemographics}
            onChange={(e) => onChange('audienceDemographics', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Free text for specific observations"
          />

          {/* EXISTING: Audience Engagement */}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Audience Engagement</InputLabel>
            <Select
              value={audienceFeedback.audienceEngagement}
              onChange={(e) => onChange('audienceEngagement', e.target.value)}
              label="Audience Engagement"
              disabled={!editMode}
            >
              <MenuItem value="excellent">Excellent</MenuItem>
              <MenuItem value="good">Good</MenuItem>
              <MenuItem value="fair">Fair</MenuItem>
              <MenuItem value="poor">Poor</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          {/* NEW: Noteworthy Categories (Multiple Select) */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Noteworthy Quick Capture</InputLabel>
            <Select
              multiple
              value={audienceFeedback.noteworthyCategories || []}
              onChange={(e) => onChange('noteworthyCategories', e.target.value)}
              input={<OutlinedInput label="Noteworthy Quick Capture" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              disabled={!editMode}
            >
              {noteworthyCategories.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* EXISTING: Noteworthy Free Text (Backwards Compatible) */}
          <TextField
            label="Noteworthy Details"
            multiline
            rows={3}
            fullWidth
            value={audienceFeedback.noteworthy}
            onChange={(e) => onChange('noteworthy', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Free text for specific details"
          />

          {/* NEW: Complaint Categories (Multiple Select) */}
          <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
            <InputLabel>Complaint Categories</InputLabel>
            <Select
              multiple
              value={audienceFeedback.complaintCategories || []}
              onChange={(e) => onChange('complaintCategories', e.target.value)}
              input={<OutlinedInput label="Complaint Categories" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              disabled={!editMode}
            >
              {complaintCategories.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* EXISTING: Complaints Free Text (Backwards Compatible) */}
          <TextField
            label="Complaint Details"
            multiline
            rows={3}
            fullWidth
            value={audienceFeedback.complaints}
            onChange={(e) => onChange('complaints', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Free text for specific details"
          />

          {/* NEW: Suggestion Categories (Multiple Select) */}
          <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
            <InputLabel>Suggestion Categories</InputLabel>
            <Select
              multiple
              value={audienceFeedback.suggestionCategories || []}
              onChange={(e) => onChange('suggestionCategories', e.target.value)}
              input={<OutlinedInput label="Suggestion Categories" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              disabled={!editMode}
            >
              {suggestionCategories.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* EXISTING: Suggestions Free Text (Backwards Compatible) */}
          <TextField
            label="Suggestion Details"
            multiline
            rows={3}
            fullWidth
            value={audienceFeedback.suggestions}
            onChange={(e) => onChange('suggestions', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Free text for specific details"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AudienceFeedbackSection;